<template>
  <div class="expertise-category">
    <div
      ref="categoryFeedRef"
      class="category-feed__main"
      :class="{ '-not-found': !state.posts.length }"
    >
      <div class="container-2024">
        <Breadcrumbs
          class="expertise-category__breadcrumbs"
          :with-grid="true"
          items-class="g-col-lg-6 g-start-lg-4 g-col-full"
        />
        <div
          v-if="state.title.length || state.description.length"
          class="category-feed__header"
        >
          <div class="container grid-2024">
            <h1 class="category-feed__title h2 g-col-full g-start-lg-4 g-col-lg-6">
              {{ state.title }}
            </h1>
            <div
              class="category-feed__intro t1 g-col-full g-start-lg-4 g-col-lg-6"
              v-html="state.description"
            />
          </div>
        </div>

        <Search
          :search="state.search"
          :alias-all="aliasAll"
          @update="onSearchUpdate"
          @reload="reloadCards"
        />

        <div
          v-if="state.loading && !state.posts.length"
          class="category-feed__grid grid-2024"
        >
          <template v-for="i in SKELETON_COUNT" :key="`skeleton-${i}`">
            <div
              v-if="calcLargeCardPositionByDevice(i - 1)"
              class="card -large -standard -l -stretch-x g-col-md-8 g-col-lg-6 -skeleton"
            />
            <div
              v-else
              class="card -small -article -m -stretch-x g-col-md-4 g-col-lg-3 -skeleton"
            />
          </template>
        </div>
        <div v-else-if="state.posts.length" class="category-feed__grid grid-2024">
          <template v-for="(card, i) in state.posts" :key="card.id ?? i">
            <CardLarge
              v-if="calcLargeCardPositionByDevice(i)"
              :post="card"
              @tag-click="onCardTagClick"
            />
            <CardSmall v-else :post="card" @tag-click="onCardTagClick" />
          </template>
        </div>
        <div v-else class="category-feed-not-found">
          <div class="container grid-2024">
            <div
              class="category-feed-not-found__content g-col-full g-start-lg-4 g-col-lg-6"
            >
              <h3
                class="category-feed-not-found__title h3"
                v-html="t('not_found_title')"
              />
              <div
                class="category-feed-not-found__intro t2"
                v-html="t('not_found_intro')"
              />
            </div>
          </div>
        </div>

        <button
          v-if="isShowBtnLoad"
          type="button"
          class="category-feed__btn-load button-2024 -m -outline"
          :disabled="state.loading"
          @click.prevent="loadPosts"
        >
          {{ t('more') }}
        </button>
      </div>
    </div>

    <div class="group-section">
      <section class="pers-consultation">
        <div class="container-2024 grid-2024">
          <FormPersConsultation
            class="g-start-ld-4 g-col-ld-6 g-col-full"
            sid="subscribe_analytics_non_qualified"
            @after-submit="handleFormSuccess"
          />
        </div>
      </section>
    </div>

    <!-- Модальное окно успеха -->
    <Modal
      v-if="isSuccessOpen"
      view="classic"
      width="md"
      :title="modal.title"
      :overlay="true"
      theme="light"
      @on-close="() => modals.toggle('success', false)"
    >
      <p
        v-if="modal.description.length"
        class="dialog-qualified-investor__desc t1"
        v-html="modal.description"
      />
    </Modal>
  </div>
</template>

<script setup lang="ts">
  import {
    ref,
    reactive,
    computed,
    createError,
    onMounted,
    onBeforeUnmount,
    nextTick,
    useState,
    useI18n,
    useRoute,
    definePageMeta,
  } from '#imports';
  import { Breadcrumbs } from '@nuxt-shared/ui/breadcrumbs';
  import FormPersConsultation from '@nuxt-features/form-pers-consultation/FormPersConsultation.vue';
  import { Modal } from '@nuxt-shared/ui/modal';
  import { useModalStore } from '@nuxt-shared/composables/stores/useModalStore';
  import { CardLarge, CardSmall, Search } from './ui';
  import type { CategoryFeedSearchItem } from './ui';
  import type { CategoryFeedState } from './types';
  import { ALL_CATEGORY, getPosts } from './model';

  /*=== Localization ===*/
  const { t } = useI18n({ useScope: 'local' });

  /*=== Route ===*/
  const route = useRoute();
  const category = computed(() => route.params.category as string);

  // Пересоздаём страницу при смене категории, иначе при клиентской навигации
  // (тот же компонент роута) setup не перезапускается и лента не перезагружается.
  definePageMeta({
    key: (pageRoute) => pageRoute.params.category as string,
  });

  /*=== Constants ===*/
  const aliasAll = t('all');

  /*=== State ===*/
  const categoryFeedRef = ref<HTMLElement | null>(null);
  const deviceWidth = ref(0);

  /** Сколько скелетонов отрисовывать при `reloadCards`. Совпадает с одной страницей выдачи. */
  const SKELETON_COUNT = 11;

  /** Дебаунс между фильтром и фактическим запросом (мс). Аналог легасишного. */
  const RELOAD_DEBOUNCE_MS = 500;

  let reloadTimeoutId: ReturnType<typeof setTimeout> | null = null;

  const routeTagsParam = (route.query.TAGS as string) || '';

  const initialGroup = routeTagsParam
    ? routeTagsParam.split(',').map((s) => s.trim())
    : [];

  const state = useState<CategoryFeedState>(
    `expertise-category-${category.value}`,
    () => ({
      title: '',
      filter: {},
      posts: [],
      description: '',
      page: {
        next: 1,
        count: 0,
      },
      route: {
        q: (route.query.q as string) || '',
        tags: (route.query.TAGS as string) || '',
        clear_cache: (route.query.clear_cache as string) || null,
        group: initialGroup,
      },
      search: {
        value: (route.query.q as string) || '',
        group: initialGroup,
        items: [],
      },
      tags: {
        list: [],
        clickable: false,
        path: '',
        quantity: {},
      },
      init: false,
      loading: false,
    }),
  );

  /*=== Computed ===*/
  const isShowBtnLoad = computed(
    () => state.value.page.count >= state.value.page.next && state.value.posts.length > 0,
  );

  /**
   * Пересобирает список тегов для `<Search>` (аналог легасишного `updateTagsList`).
   * - чистит `state.search.group` от тегов, которых нет в актуальном списке от бэка;
   * - для каждого доступного тега формирует элемент с флагом `selected` и количеством.
   */
  const updateTagsList = (
    queryTags: string[],
    tagsList: string[],
    tagsQuantity: Record<string, number>,
  ) => {
    const isAll = !queryTags.length || queryTags.includes(aliasAll);
    const filteredGroup = queryTags.filter((tag) => tagsList.includes(tag));
    state.value.search.group = filteredGroup;

    if (!tagsList.length) {
      state.value.search.items = [];
      return;
    }

    const items: CategoryFeedSearchItem[] = tagsList.map((alias, index) => ({
      id: index + 1,
      alias,
      name: alias,
      quantity: tagsQuantity[alias],
      disabled: false,
      selected: !isAll && filteredGroup.includes(alias),
    }));

    state.value.search.items = items;
  };

  /**
   * Подгружает следующую страницу публикаций, дописывает её в общий `state`
   * и увеличивает `page.next` на один.
   *
   * Поведение:
   * - сбрасывает бэк-кэш только на самой первой загрузке (`clearCache` из URL),
   *   все последующие — `'N'`;
   * - дедуплицирует по `id`, чтобы повторные клики «ещё» не плодили карточки;
   * - выставляет `state.loading` на время запроса (для скелетонов и блокировки «ещё»).
   */
  const loadPosts = async () => {
    // Нет смысла грузить страницу, которой нет (после первой загрузки знаем общее число).
    if (state.value.init && state.value.page.next > state.value.page.count) return;

    state.value.loading = true;

    try {
      const data = await getPosts(
        {
          moreLink: `/expertise/${category.value}/`,
          p: state.value.page.next,
          q: state.value.search.value,
          queryTags: state.value.search.group,
          clearCache: state.value.init ? 'N' : state.value.route.clear_cache,
        },
        category.value,
      );

      if (!data) return;

      if (state.value.page.next === 1) {
        state.value.title = data.title;
        state.value.description = data.description;
        state.value.page.count = data.page.count;
      }

      state.value.filter = data.filter;
      state.value.tags = data.tags;

      // Дедуп по id: защита от гонок / повторного «ещё» / двойного клика.
      const knownIds = new Set(state.value.posts.map((p) => p.id));
      const fresh = data.posts.filter((p) => !knownIds.has(p.id));
      state.value.posts = [...state.value.posts, ...fresh];

      updateTagsList(state.value.search.group, data.tags.list, data.tags.quantity);

      state.value.page.next += 1;
      state.value.init = true;
    } finally {
      state.value.loading = false;
    }
  };

  /**
   * Пишет текущие фильтры (`q` и `TAGS`) обратно в URL через `history.pushState`,
   * чтобы ссылка была шеришабельной и состояние сохранялось при перезагрузке.
   * Аналог легасишного `updateUrlTags`. SSR-safe (на сервере просто выходит).
   */
  const updateUrlTags = () => {
    if (import.meta.server) return;

    const url = new URL(window.location.href);
    const checkedOptions = state.value.search.group.join(',');

    if (!checkedOptions || checkedOptions === aliasAll) {
      url.searchParams.delete('TAGS');
    } else {
      url.searchParams.set('TAGS', checkedOptions);
    }

    if (state.value.search.value) {
      url.searchParams.set('q', state.value.search.value);
    } else {
      url.searchParams.delete('q');
    }

    history.pushState(null, '', url.href);
  };

  /**
   * Перезагружает ленту с первой страницы — для смены фильтров/поиска.
   *
   * - дебаунсит 500мс (повторные вызовы внутри окна — отменяют предыдущий setTimeout);
   * - сразу выставляет `loading=true` и чистит `posts`, чтобы шаблон показал скелетоны
   *   и не оставлял на экране карточки прошлой выборки;
   * - после ответа синхронизирует URL и скроллит к началу ленты (только если уже была
   *   хотя бы одна загрузка — на самом первом рендере скролл не нужен).
   */
  const reloadCards = () => {
    if (reloadTimeoutId) clearTimeout(reloadTimeoutId);

    // Заранее покажем скелетон — иначе пока тикает дебаунс лента «застывает» как есть.
    state.value.loading = true;
    state.value.posts = [];

    reloadTimeoutId = setTimeout(async () => {
      state.value.page.next = 1;
      await loadPosts();
      updateUrlTags();

      if (state.value.init) {
        await nextTick();
        categoryFeedRef.value?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    }, RELOAD_DEBOUNCE_MS);
  };

  /** Подменяет фильтры в state без перезагрузки (Search эмитит `update`). */
  const onSearchUpdate = ({ value, group }: { value: string; group: string[] }) => {
    state.value.search.value = value;
    state.value.search.group = group;
  };

  /**
   * Клик по тегу карточки — сбрасывает поиск и оставляет только этот тег
   * (поведение легасишного CardLarge/CardSmall + setFiltersSelection('', tag)).
   * Игнорируется, если бэк явно отметил теги как некликабельные.
   */
  const onCardTagClick = (tag: string) => {
    if (!state.value.tags.clickable) return;
    state.value.search.value = '';
    state.value.search.group = [tag];
    reloadCards();
  };

  /*=== Initial load ===*/
  // На сервере — всегда (SSR). На клиенте — только если данных ещё нет: при гидрации
  // `init` уже `true` (повторно не грузим, нет mismatch), а у новой категории после
  // клиентской навигации state свежий (`init=false`) — и тогда грузим на клиенте.
  if (import.meta.server || !state.value.init) {
    await loadPosts();
  }

  // Заголовок бэкенд отдаёт даже при пустой выдаче (например, когда фильтр
  // ничего не нашёл), поэтому пустой `title` означает именно отсутствие такой
  // категории. Без этой проверки /expertise/<что угодно> отвечал бы 200 с
  // пустой лентой. Сводная категория — исключение: своего заголовка у неё нет.
  if (!state.value.title && category.value !== ALL_CATEGORY) {
    throw createError({ statusCode: 404, fatal: true });
  }

  /*=== Cards ===*/
  /**
   * Считает «большие» карточки по индексу в зависимости от ширины вьюпорта.
   * Опирается только на реактивный `deviceWidth` (а не на `window.innerWidth`),
   * чтобы SSR и первый клиентский рендер давали одинаковый DOM (нет hydration mismatch).
   * Реальная ширина проставляется в `onMounted`, и Vue реактивно пересчитает раскладку.
   *
   * Границы синхронизированы с брейкпоинтами `.grid-2024` (`_grid.scss`):
   * - <768 — карточек в одну колонку, больших нет;
   * - 768–991 — 8-колоночный грид, `g-col-md-8/4` → большая 3-я (i=2, span 8 = вся строка),
   *   далее каждая 11-я;
   * - >=992  — 12-колоночный грид, большая 2-я (i=1), далее каждая 11-я. С 992 до 1279
   *   работают классы `g-col-md-8/4` (span 8/4 из 12), с 1280 подключаются `g-col-lg-6/3`
   *   (span 6/3 из 12) — в обоих случаях `small + large` укладываются в 12 колонок.
   */
  const calcLargeCardPositionByDevice = (i: number): boolean => {
    if (deviceWidth.value < 768) return false;
    if (deviceWidth.value >= 992) {
      return i === 1 || (i - 1) % 11 === 0;
    }
    return i === 2 || (i - 2) % 11 === 0;
  };

  const onResize = () => {
    deviceWidth.value = window.innerWidth;
  };

  onMounted(() => {
    deviceWidth.value = window.innerWidth;
    window.addEventListener('resize', onResize);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize);
    if (reloadTimeoutId) clearTimeout(reloadTimeoutId);
  });

  /*=== Pers consultation modal ===*/
  const modal = reactive({
    title: '',
    description: '',
  });

  const modals = useModalStore(['success']);
  const isSuccessOpen = computed(() => modals.status('success'));

  const handleFormSuccess = ({
    title: t,
    description: d,
  }: {
    title: string;
    description: string;
  }) => {
    modal.title = t;
    modal.description = d;
    modals.toggle('success', true);
  };
</script>

<style lang="scss">
  @use './style';
</style>

<i18n lang="json" src="./locale.json" />
